import type { TaskResult } from "../types";

function normalizeFormula(value: string | undefined): string {
  return (value ?? "")
    .replace(/\$/g, "")
    .replace(/\s+/g, "")
    .replace(/^=/, "")
    .toUpperCase();
}

export async function gradeProject13(): Promise<TaskResult[]> {
  const results: TaskResult[] = [];

  try {
    await Excel.run(async (context) => {
      const namedItem = context.workbook.names.getItemOrNullObject("Precios");
      namedItem.load("formula");
      await context.sync();

      const formula = normalizeFormula(
        namedItem.isNullObject ? "" : namedItem.formula
      );
      // Must reference 'Lista de precios' sheet, start at D5 row 15, and D18 row 26
      // Allow wider column ranges (e.g. D5:F15 instead of D5:D15)
      const refersToSheet = formula.includes("'LISTADEPRECIOS'!");
      const hasRange1 = /D5:[A-Z]*15/.test(formula);
      const hasRange2 = /D18:[A-Z]*26/.test(formula);
      const passed = refersToSheet && hasRange1 && hasRange2;
      results.push({ taskId: 1, passed });
    });
  } catch {
    results.push({ taskId: 1, passed: false });
  }

  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem("Lista de precios");
      const cell = sheet.getRange("H5");
      cell.load("formulas,values");
      await context.sync();

      const formula = normalizeFormula(String(cell.formulas[0][0] ?? ""));
      // Only check that formula uses SUM with the 3 named ranges
      // (value changes after sorting in task 5)
      const passed =
        (formula.includes("SUM(") || formula.includes("SUMA(")) &&
        formula.includes("PRECIO_10G") &&
        formula.includes("INSTALACI\u00D3N_10G") &&
        formula.includes("SOPORTE_10G");

      results.push({ taskId: 2, passed });
    });
  } catch {
    results.push({ taskId: 2, passed: false });
  }

  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem("Lista de precios");
      const formats = sheet.getUsedRange().conditionalFormats;
      formats.load("items");
      await context.sync();

      results.push({ taskId: 3, passed: formats.items.length === 0 });
    });
  } catch {
    results.push({ taskId: 3, passed: false });
  }

  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem("Lista de precios");
      const range1 = sheet.getRange("C4:F4");
      const range2 = sheet.getRange("C17:F17");
      range1.format.load("textOrientation");
      range2.format.load("textOrientation");
      await context.sync();

      // Only ascending angle (45°) is correct, not descending (-45°)
      const passed =
        range1.format.textOrientation === 45 &&
        range2.format.textOrientation === 45;

      results.push({ taskId: 4, passed });
    });
  } catch {
    results.push({ taskId: 4, passed: false });
  }

  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem("Lista de precios");
      const range = sheet.getRange("B5:C15");
      range.load("values");
      await context.sync();

      const rows = range.values.map((row) => [
        String(row[0] ?? "").trim(),
        String(row[1] ?? "").trim(),
      ]);

      const sorted = [...rows].sort((a, b) => {
        const byName = a[0].localeCompare(b[0], "es", { sensitivity: "base" });
        if (byName !== 0) return byName;
        return a[1].localeCompare(b[1], "es", { sensitivity: "base" });
      });

      const passed = rows.every(
        (row, index) => row[0] === sorted[index][0] && row[1] === sorted[index][1]
      );

      results.push({ taskId: 5, passed });
    });
  } catch {
    results.push({ taskId: 5, passed: false });
  }

  return results;
}
